import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { TeamMember } from '@/lib/api/services/teamService';
import { primaryMenuItems, standaloneNavItems } from '@/components/layout/AppSidebar';
import { useToast } from '@/hooks/use-toast';
import { showApiError } from '@/lib/errors/showApiError';

// Map sidebar menu IDs to a permission-dialog group label. Any ID added to
// AppSidebar that's not listed here falls under "Other" so a newly-shipped
// feature is always visible to admins assigning permissions, even before
// this map gets a curated entry.
const GROUP_BY_ID: Record<string, string> = {
  overview: 'General',
  inbox: 'General',
  marketing: 'Marketing',
  customers: 'Marketing',
  automation: 'Marketing',
  ads: 'Marketing',
  instagram: 'Marketing',
  youtube: 'Marketing',
  scheduling: 'Operations',
  store: 'Operations',
  'website-builder': 'Operations',
  'data-store': 'Operations',
  petpooja: 'Operations',
  reports: 'Analytics',
  'ai-tools': 'Tools',
  settings: 'Admin',
  help: 'Admin',
};

const GROUP_ORDER = ['General', 'Marketing', 'Operations', 'Analytics', 'Tools', 'Admin', 'Other'];

const standaloneIdFromUrl = (url: string): string => {
  if (url === '/home') return 'overview';
  if (url === '/home/inbox') return 'inbox';
  return url;
};

type MenuEntry = { id: string; label: string; group: string };

const MENU_ITEMS: MenuEntry[] = [
  ...standaloneNavItems.map((i) => {
    const id = standaloneIdFromUrl(i.url);
    return { id, label: i.title, group: GROUP_BY_ID[id] || 'Other' };
  }),
  ...primaryMenuItems.map((i) => ({
    id: i.id,
    label: i.title,
    group: GROUP_BY_ID[i.id] || 'Other',
  })),
  // Admin sections (Account Settings, Support) are intentionally NOT grantable
  // here: their admin sub-pages are role-locked (never available to USER/AGENT),
  // and General Settings + Support are always available regardless of permissions.
  // Showing them as toggles would be misleading — toggling them does nothing.
].filter((i) => i.group !== 'Admin');

const GROUPS = GROUP_ORDER.filter((g) => MENU_ITEMS.some((i) => i.group === g));

const ALL_IDS = new Set(MENU_ITEMS.map(i => i.id));

interface MemberPermissionsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  member: TeamMember;
  onSave: (memberId: string, menuPermissions: string[] | null) => Promise<void>;
}

export const MemberPermissionsDialog: React.FC<MemberPermissionsDialogProps> = ({
  open,
  onOpenChange,
  member,
  onSave,
}) => {
  const { toast } = useToast();
  const [selected, setSelected] = useState<Set<string>>(new Set(ALL_IDS));
  const [saving, setSaving] = useState(false);

  const getMenuPermissions = (m: TeamMember): string[] | null => {
    let prefs = (m as any).preferences;
    if (typeof prefs === 'string') {
      try { prefs = JSON.parse(prefs); } catch { prefs = null; }
    }
    const mp = prefs?.menu_permissions ?? (m as any).menu_permissions;
    return Array.isArray(mp) ? mp : null;
  };

  useEffect(() => {
    if (open) {
      const perms = getMenuPermissions(member);
      setSelected(perms ? new Set(perms) : new Set(ALL_IDS));
    }
  }, [open, member]);

  const toggle = (id: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleGroup = (group: string) => {
    const groupIds = MENU_ITEMS.filter(i => i.group === group).map(i => i.id);
    const allChecked = groupIds.every(id => selected.has(id));
    setSelected(prev => {
      const next = new Set(prev);
      groupIds.forEach(id => allChecked ? next.delete(id) : next.add(id));
      return next;
    });
  };

  const selectAll = () => setSelected(new Set(ALL_IDS));
  const deselectAll = () => setSelected(new Set());

  // Count only menu sections (not integration sub-permissions) so the
  // "N of M sections enabled" label can never exceed MENU_ITEMS.length.
  const selectedSectionCount = MENU_ITEMS.filter(i => selected.has(i.id)).length;

  const allSelected = MENU_ITEMS.every(i => selected.has(i.id));

  const handleSave = async () => {
    if (saving) return;

    // Always save the explicit full array for proper role override.
    // null would mean "use role defaults" which is limited for agents.
    // We want explicit full access to override role defaults.
    const all = Array.from(selected);

    // Strip any stale/unknown ids that may have been seeded from the member's
    // stored permissions — a sidebar section that was since renamed/removed, or
    // an integration key dropped from INTEGRATION_CONFIGS. These must NEVER block
    // a legitimate save (that was the "Save button does nothing" bug); drop them
    // silently and keep going.
    const permissions = all.filter(id => ALL_IDS.has(id));
    const dropped = all.filter(id => !ALL_IDS.has(id));
    if (dropped.length > 0) {
      console.warn('[MemberPermissions] dropping unknown permission ids:', dropped);
    }

    // Backend rejects an empty array (null = full access, [] is not allowed).
    // Surface this to the user instead of silently throwing.
    if (permissions.length === 0) {
      toast({
        title: 'Select at least one section',
        description: 'Grant at least one section, or click "Select All" for full access.',
        variant: 'destructive',
      });
      return;
    }

    setSaving(true);
    try {
      await onSave(member.id, permissions);
      onOpenChange(false);
    } catch (error: any) {
      // onSave handles its own toasts on the happy/expected-error paths; this
      // is a safety net so an unexpected throw never silently kills the button
      // or leaks an unhandled promise rejection.
      console.error('Error saving permissions:', error);
      showApiError(error, { fallback: { title: "Couldn't save permissions", description: 'Failed to save permissions. Please try again.' } });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Menu Permissions</DialogTitle>
          <p className="text-sm text-muted-foreground">
            Choose which sidebar sections <strong>{member.name}</strong> can access.
          </p>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Select all / Deselect all */}
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground">
              {selectedSectionCount} of {MENU_ITEMS.length} sections enabled
              {allSelected && <span className="ml-1 text-green-600">(full access)</span>}
            </span>
            <div className="flex gap-2">
              <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={selectAll}>
                Select All
              </Button>
              <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={deselectAll}>
                Deselect All
              </Button>
            </div>
          </div>

          {/* Menu list grouped */}
          <div className="space-y-4">
            {GROUPS.map(group => {
              const groupItems = MENU_ITEMS.filter(i => i.group === group);
              const allChecked = groupItems.every(i => selected.has(i.id));
              const someChecked = groupItems.some(i => selected.has(i.id));

              return (
                <div key={group}>
                  <div
                    className="flex items-center gap-2 mb-2 cursor-pointer"
                    onClick={() => toggleGroup(group)}
                  >
                    <Checkbox
                      checked={allChecked}
                      ref={(el) => {
                        if (el) (el as any).indeterminate = !allChecked && someChecked;
                      }}
                      onCheckedChange={() => toggleGroup(group)}
                      onClick={e => e.stopPropagation()}
                    />
                    <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                      {group}
                    </span>
                  </div>
                  <div className="ml-6 space-y-2">
                    {groupItems.map(item => (
                      <div key={item.id} className="flex items-center gap-2">
                        <Checkbox
                          id={item.id}
                          checked={selected.has(item.id)}
                          onCheckedChange={() => toggle(item.id)}
                        />
                        <Label htmlFor={item.id} className="cursor-pointer text-sm">
                          {item.label}
                        </Label>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save Permissions'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
